import type { ImagePickerListItem, ImagePickerListResponse } from '../../api'

export interface ImagePickerDeleteRollback {
  items: ImagePickerListItem[]
  total: number | null
  summary: ImagePickerListResponse['summary'] | null
  selectedIds: string[]
  activeAssetId: string | null
  page: number
}

function decrementCount(map: Record<string, number>, key: string): void {
  if (map[key] == null) return
  map[key] = Math.max(0, map[key] - 1)
}

/** Optimistically adjust sidebar filter counts after one asset is removed. */
export function decrementPickerSummary(
  summary: ImagePickerListResponse['summary'] | null,
  item: ImagePickerListItem,
): ImagePickerListResponse['summary'] | null {
  if (!summary) return null
  const resource_types = { ...summary.resource_types }
  const sources = { ...summary.sources }
  decrementCount(resource_types, 'all')
  decrementCount(resource_types, item.resource_type)
  decrementCount(sources, 'all')
  decrementCount(sources, item.source)
  return { resource_types, sources }
}

export function buildPickerDeleteRollback(input: {
  items: ImagePickerListItem[]
  total: number | null
  summary: ImagePickerListResponse['summary'] | null
  selectedIds: string[]
  activeAssetId: string | null
  page: number
}): ImagePickerDeleteRollback {
  return {
    items: input.items,
    total: input.total,
    summary: input.summary
      ? {
          resource_types: { ...input.summary.resource_types },
          sources: { ...input.summary.sources },
        }
      : null,
    selectedIds: [...input.selectedIds],
    activeAssetId: input.activeAssetId,
    page: input.page,
  }
}

export function applyOptimisticPickerDelete(input: {
  items: ImagePickerListItem[]
  total: number | null
  summary: ImagePickerListResponse['summary'] | null
  selectedIds: string[]
  itemId: string
}): {
  items: ImagePickerListItem[]
  total: number | null
  summary: ImagePickerListResponse['summary'] | null
  selectedIds: string[]
  remainingOnPage: number
} {
  const item = input.items.find((i) => i.id === input.itemId)
  const items = input.items.filter((i) => i.id !== input.itemId)
  const total = input.total == null ? null : Math.max(0, input.total - 1)
  const summary = item ? decrementPickerSummary(input.summary, item) : input.summary
  const selectedIds = input.selectedIds.filter((id) => id !== input.itemId)
  return { items, total, summary, selectedIds, remainingOnPage: items.length }
}
